/*#
# theme-editor

**A live editor for the `--w3d-*` palette.** Every token that a widget actually
consumes, as a grid of controls: colours, metrics with a slider *and* a number
field, and a font menu. Change one and your rebuild callback fires.

## Why it is a component rather than a demo

An adopter theming an app wants exactly this UI, and copying it out of a doc
comment is how it drifts from the palette it edits. `w3dTheme` is the one place
the tokens are declared; this is the one place they are edited. The live demo
is on [w3d-theme](/w3d-theme/).

It is a plain DOM component in its own module, so it tree-shakes out of a build
that never imports it. Nothing else in the library depends on it.

## Rebuilding is the caller's job

The theme is read when a widget is **built** (why is on
[w3d-theme](/w3d-theme/)). So this cannot repaint your UI, and does not
pretend to: it applies the change and calls `handleChange`, and you rebuild
whatever you are showing.

## The colour input is injected

Alpha matters here — `panelBg`, `rowHover` and `selectedBg` are all `rgba()` —
and the native `<input type="color">` **cannot express it**, silently dropping
the channel so a translucent token looks broken rather than untouched.

This is a DOM component, and tosijs-ui is deliberately not a dependency of this
library. So the control is a parameter: pass `colorInput` (tosijs-ui's does
alpha), or accept the native fallback and know what it costs.
*/
/*{ "parent": "w3d-theme", "order": 10 }*/

import { handlerOf } from './handler-of.js'
import { elements } from 'tosijs'
import { setW3dTheme, w3dTheme, type W3dTheme } from './w3d-theme.js'

const { div, label, select, option, span, input, h3 } = elements

/**
 * One metric for every control, so the rows line up.
 *
 * A palette editor is read DOWN a column — you are comparing values, not using
 * one control — and controls at their natural heights make that a ragged list
 * where every row costs a little attention. `boxSizing` is what makes the
 * height actually equal across an input, a select and a colour swatch, whose
 * default padding and borders differ.
 *
 * Explicit colours rather than inherited: the editor lands on whatever surface
 * a host has, and a field inheriting a pale text colour from a dark page
 * renders light-grey-on-white.
 */
const CONTROL_HEIGHT = '26px'
const FIELD_STYLE = {
  background: '#fff',
  color: '#111',
  border: '1px solid #8b8b8b',
  borderRadius: '4px',
  padding: '0 5px',
  height: CONTROL_HEIGHT,
  boxSizing: 'border-box',
  font: '13px system-ui',
  minWidth: '0',
}

/** Tokens the widgets read today. Reserved ones are deliberately absent — see `w3d-theme`. */
const COLOURS: Array<keyof W3dTheme> = [
  'panelBg',
  'text',
  'muted',
  'accent',
  'rowBg',
  'rowHover',
  'buttonBg',
  'buttonHover',
  'buttonActive',
  'buttonActiveText',
  'track',
  'caret',
  'placeholder',
]

/** `[token, min, max, step]` — the metrics, all live. */
const METRICS: Array<[keyof W3dTheme, number, number, number]> = [
  ['fontSize', 10, 28, 1],
  ['roundedRadius', 0, 24, 1],
  ['padding', 0, 24, 1],
  ['spacing', 0, 24, 1],
  ['strokeWidth', 0.5, 6, 0.5],
  ['lineHeight', 1, 2, 0.05],
]

/**
 * Generic families first — they always resolve, whatever the platform — then
 * faces that ship on both macOS and Windows.
 *
 * Every chain ends in a generic on purpose: "installed on both" is a weaker
 * promise than it sounds even across OS versions, and elsewhere it is no
 * promise at all. A chain ending in `serif` degrades to something readable
 * rather than to whatever the browser happens to pick.
 */
export const FONT_STACKS = [
  'system-ui, sans-serif',
  'sans-serif',
  'serif',
  'monospace',
  'Georgia, serif',
  'Helvetica, Arial, sans-serif',
  '"Times New Roman", Times, serif',
  '"Courier New", Courier, monospace',
  'Verdana, Geneva, sans-serif',
  '"Trebuchet MS", Tahoma, sans-serif',
  'Palatino, "Palatino Linotype", "Book Antiqua", serif',
  'Impact, Haettenschweiler, sans-serif',
  /*
  A WEB font, to show that a theme is not limited to what is installed.

  It only renders if the host has actually loaded it — a font family is a
  request, not a guarantee, and `w3dTheme.fontFamily` is just a string handed to
  SVG. The demo loads it from Google Fonts; a consumer picking this stack
  without loading the face gets the `serif` fallback, which is the honest
  outcome rather than a broken one.
  */
  'Rosario, serif',
]

/**
 * Labels where the family name alone would mislead.
 *
 * Rosario is the only entry that needs the host to have loaded something, and
 * it is the only one that will not render on an in-scene panel — so the menu
 * says so rather than leaving you to discover it.
 */
const FONT_LABELS: Record<string, string> = {
  'Rosario, serif': 'Rosario (Google Web Font)',
}

export interface ThemeEditorOptions {
  /** Heading above the grid. Pass `''` for none. */
  title?: string
  /** Fired after each change, so you can rebuild whatever you are showing. */
  handleChange?: (theme: W3dTheme) => void
  /**
   * Colour control factory. Defaults to `<input type="color">`, which **cannot
   * express alpha** — pass tosijs-ui's `colorInput` (or any alpha-capable
   * control) when you have one.
   */
  colorInput?: (config: {
    value: string
    /** Passed so an injected control can match the editor's row height. */
    height?: string
    /** Called with the new colour — or with a DOM event, which is unwrapped for you. */
    onChange: (value: string | Event) => void
  }) => HTMLElement
  /** Restrict to a subset of tokens. Defaults to everything live. */
  colours?: Array<keyof W3dTheme>
  metrics?: Array<[keyof W3dTheme, number, number, number]>
}

/**
 * Build the editor. Returns a plain element — put it wherever you like.
 *
 * The grid is two columns (label, control) so the controls line up down the
 * page; a list of `label: control` pairs at natural width does not, and a
 * palette you are comparing values across is exactly where that shows.
 */
export function themeEditor(
  config: ThemeEditorOptions = {}
): ThemeEditorElement {
  // What the page looked like before anyone touched a control — see `restore`.
  const snapshot = { ...w3dTheme }
  const {
    title = 'Theme Editor',
    colours = COLOURS,
    metrics = METRICS,
  } = config

  /*
  `handleChange`. (`onChange` was the old alias, removed in 0.10.)

  This is the `onFoo` footgun landing on a plain factory rather than a
  component: the option was named `onChange` while every caller — the w3d-theme
  demo included — passed `handleChange`, which is the convention the rest of
  this library follows. Nothing errored. The editor went on writing the theme
  table, and the callback that rebuilds the panels was simply never called, so
  the controls moved and the thing they were editing did not.
  */
  const changed = (): void =>
    handlerOf<(theme: W3dTheme) => void>(
      config as Record<string, unknown>,
      'handleChange',
      'onChange'
    )?.(w3dTheme)

  /**
   * Accept either a value or a DOM event from an injected control.
   *
   * The contract is easy to get wrong in one specific way, and I got it wrong
   * first: a tosijs `Component` binds any `on*` prop as a **DOM event
   * listener** (CLAUDE.md names this footgun explicitly), so an injected
   * `colorInput({onChange})` calls back with an `Event`, not a string. Setting
   * a token to an event object is not an error anywhere — it stringifies, fails
   * to parse as a colour, and the widget paints **black**. Tonio: "some of the
   * colors are just turning black once I set them."
   *
   * Normalising here rather than documenting the contract is deliberate: the
   * failure is silent and looks like a theme bug rather than a wiring bug, and
   * every consumer injecting a control would meet it once.
   */
  const asColor = (v: unknown): string | null => {
    if (typeof v === 'string') return v || null
    const target = (v as { target?: { value?: unknown } } | null)?.target
    return typeof target?.value === 'string' ? target.value : null
  }

  const nativeColor = (c: { value: string; onChange: (v: string) => void }) =>
    input({
      type: 'color',
      value: c.value,
      onInput(evt: Event) {
        c.onChange((evt.target as HTMLInputElement).value)
      },
    }) as HTMLElement
  const makeColor = config.colorInput ?? nativeColor

  const grid = div({
    class: 'w3d-theme-editor',
    style: {
      display: 'grid',
      // `max-content` for labels so they never wrap, `1fr` for controls so the
      // editor FILLS its column instead of sitting at some natural width.
      gridTemplateColumns: 'max-content 1fr',
      gap: '7px 12px',
      alignItems: 'center',
      width: '100%',
      maxWidth: '380px',
      font: '13px system-ui',
    },
  })
  if (title) {
    grid.append(
      h3(
        {
          style: {
            gridColumn: '1 / -1',
            margin: '0 0 6px',
            font: '600 14px system-ui',
            opacity: '0.85',
            textAlign: 'center',
          },
        },
        title
      )
    )
  }

  const row = (name: string, control: HTMLElement): void => {
    // Stretch, so a colour swatch, a select and a slider all end the same
    // distance from the right edge. Ragged right-hand ends are what make a list
    // of controls read as untidy even when each one is fine on its own.
    // Height too, not just width. An injected control (tosijs-ui's colour input)
    // and a native range both come with their own intrinsic height, so asking
    // politely via a config option is not enough — measured 32 / 30 / 26 before
    // this line. The row owns the metric.
    Object.assign(control.style, {
      justifySelf: 'stretch',
      width: '100%',
      height: CONTROL_HEIGHT,
      boxSizing: 'border-box',
    })
    grid.append(
      label({ style: { justifySelf: 'end', opacity: '0.8' } }, name),
      control
    )
  }

  for (const key of colours) {
    const current = String(w3dTheme[key])
    row(
      key,
      makeColor({
        height: CONTROL_HEIGHT,
        value: current,
        onChange: (v: unknown) => {
          const colour = asColor(v)
          if (colour == null) return
          setW3dTheme({ [key]: colour } as Partial<W3dTheme>)
          changed()
        },
      })
    )
  }

  for (const [key, min, max, step] of metrics) {
    /*
    SLIDER AND NUMBER FIELD, KEPT IN SYNC.

    A slider alone hides the value you are setting — you can see a handle
    position but not the figure under it, which is the same complaint that put
    a readout on `slider3d`. The number field also lets you type an exact value,
    which dragging cannot do at a step of 0.05.

    Each writes the other, so they can never disagree; a pair that drifts is
    worse than a slider alone, because now two things claim to be the value.
    */
    const apply = (v: number | string): void => {
      const raw = Number(v)
      if (!Number.isFinite(raw)) return
      /*
      CLAMP before storing, or the pair drifts.

      A number field accepts anything typed into it while a range silently
      clamps, so typing 34 into a metric capped at 28 left the slider reading 28
      and the field reading 34 — the exact "two things claim to be the value"
      this row is built to prevent — with the theme taking the out-of-range one.
      */
      const n = Math.min(max, Math.max(min, raw))
      setW3dTheme({ [key]: n } as Partial<W3dTheme>)
      range.value = String(n)
      num.value = String(n)
      changed()
    }
    const range = input({
      type: 'range',
      min,
      max,
      step,
      value: String(w3dTheme[key]),
      style: { width: '100%', minWidth: '0', height: CONTROL_HEIGHT },
      onInput(evt: Event) {
        apply((evt.target as HTMLInputElement).value)
      },
    }) as HTMLInputElement
    const num = input({
      type: 'number',
      min,
      max,
      step,
      value: String(w3dTheme[key]),
      /*
      Explicit colours, not inherited.

      The editor is dropped onto whatever surface a host has, and a number
      field that inherits a pale text colour from a dark page renders as
      light-grey-on-white — legible in theory and not in practice. A field
      styled like a field works on either kind of page.
      */
      style: { ...FIELD_STYLE, width: '64px', textAlign: 'right' },
      onChange(evt: Event) {
        apply((evt.target as HTMLInputElement).value)
      },
    }) as HTMLInputElement
    row(
      key,
      // A sub-grid rather than flex: `1fr 64px` puts every number field at the
      // same x, so the figures form a column you can read down.
      span(
        {
          style: {
            display: 'grid',
            gridTemplateColumns: '1fr 64px',
            gap: '8px',
            alignItems: 'center',
          },
        },
        range,
        num
      )
    )
  }

  // Each option renders in its own face — a font menu listing names in one face
  // makes you pick by memory rather than by looking.
  row(
    'fontFamily',
    select(
      {
        style: { ...FIELD_STYLE },
        onChange(evt: Event) {
          setW3dTheme({ fontFamily: (evt.target as HTMLSelectElement).value })
          changed()
        },
      },
      ...FONT_STACKS.map((f) =>
        option(
          { value: f, style: { fontFamily: f } },
          FONT_LABELS[f] ?? f.split(',')[0].replace(/"/g, '')
        )
      )
    )
  )

  const el = grid as unknown as ThemeEditorElement
  el.restore = () => {
    setW3dTheme(snapshot)
    changed()
  }
  return el
}

/**
 * The editor element, plus the one thing only the library can do reliably:
 * **put the palette back.** The editor edits the GLOBAL table (that is how a
 * whole UI re-themes live), so a settings route that mounts it and is then
 * left leaves the change behind — which is right for a settings page you
 * APPLIED and wrong for one you abandoned or previewed. `restore()` returns
 * the table to how it was when this editor was built, and calls
 * `handleChange` so you rebuild. When to call it is yours: the editor cannot
 * tell "left" from "saved".
 */
export type ThemeEditorElement = HTMLElement & { restore: () => void }
